import { VariantItem } from "@/assets/interfaces";
import RoundCheckbox from "@/components/ui/form-elements/round-checkbox";
import { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import Badge from "@/components/ui/badge";
import { FormatCurrencyFun } from "@/contexts/cart-context-v2";
import { VariantsInternalStateItem } from "../modals/select-variant";
import { getActualPrice } from "@/assets/js/utils/utils";
import useClickOutside from "@/components/hooks/useClickOutside";
import ErrorLabel from "@/components/ui/error-label";

type Props = {
  options: VariantItem[];
  formatAsCurrency: FormatCurrencyFun;
  setOption: (index: number, payload: Partial<VariantsInternalStateItem>) => void;
  internalSelected: VariantsInternalStateItem[];
  deleteOption: (index: number) => void;
  updateOptionQuantity: (index: number, quantity: number) => void;
};

const SelectImageVariant = ({
  options,
  formatAsCurrency,
  setOption,
  internalSelected,
  deleteOption,
  updateOptionQuantity,
}: Props) => {
  const [showDropUp, setShowDropUp] = useState<{ id: string; show: boolean }[]>(
    options.map((o) => ({ id: o.id, show: false }))
  );

  const variantIsSelected = (id: string) => {
    return internalSelected.some((i) => i.id === id && i.quantity > 0);
  };

  const toggleDropUp = (id: string, state?: boolean) => {
    setShowDropUp((prev) => prev.map((p) => (p.id === id ? { ...p, show: state !== undefined ? state : !p.show } : p)));
  };

  const toggleOption = (index: number, option: VariantItem) => {
    if (!option.available_to_purchase) return;

    const selectedOption = internalSelected.find((i) => i?.id === option.id && i?.quantity > 0);

    setOption(index, { id: option.id, image: option.image, quantity: selectedOption ? 0 : 1, error: "" });
    toggleDropUp(option.id, selectedOption ? false : true);
  };

  const showThisDropUp = (id: string) => {
    return showDropUp.find((s) => s.id === id)?.show ?? false;
  };

  const interceptQuantityUpdate = (index: number, quantity: number) => {
    const selectedOption = internalSelected.find((i) => i.id === options[index].id);

    updateOptionQuantity(index, quantity);

    if (selectedOption?.quantity < 1) {
      toggleDropUp(options[index].id, false);
    }
  };

  return (
    <div>
      <ErrorLabel
        error={internalSelected.reduce((acc, curr) => acc + (acc === "" ? curr.error : `, ${curr.error}`), "")}
      />
      <div className="grid items-start w-full grid-cols-[repeat(auto-fit,120px)] sm:grid-cols-[repeat(auto-fit,150px)] gap-2.5">
        {options.map((option, index) => (
          <figure
            className="relative w-full h-[120px] sm:h-[150px] rounded-10 overflow-hidden cursor-pointer"
            onClick={(e) => {
              option.available_to_purchase ? toggleOption(index, option) : null;
            }}
          >
            <img src={option.image} alt="" className="w-full object-cover h-full" />
            <div
              className={classNames("absolute inset-0", {
                "p-2": !showThisDropUp(option.id),
                "p-0": showThisDropUp(option.id),
              })}
            >
              <div className="flex items-center justify-between w-full" onClick={(e) => e.stopPropagation()}>
                {variantIsSelected(option.id) && (
                  <OverlaySelector
                    option={option}
                    index={index}
                    internalSelected={internalSelected}
                    formatAsCurrency={formatAsCurrency}
                    toggleDropUp={toggleDropUp}
                    showThisDropUp={showThisDropUp}
                    variantIsSelected={variantIsSelected}
                    deleteOption={deleteOption}
                    updateOptionQuantity={interceptQuantityUpdate}
                  />
                )}

                <div className="flex-1 flex justify-end" onClick={(e) => e.stopPropagation()}>
                  {option.available_to_purchase && (
                    <RoundCheckbox
                      name={`variant-option`}
                      id={option.id}
                      checked={variantIsSelected(option.id)}
                      // small
                      className={`ml-auto`}
                      disabled={!option.available_to_purchase}
                      onChange={(e) => (option.available_to_purchase ? toggleOption(index, option) : null)}
                    />
                  )}
                </div>
              </div>

              {!option.available_to_purchase && (
                // <span className="bg-white px-2 absolute text-accent-red-500 py-1 text-xxs right-2.5 bottom-2.5 inline-flex font-semibold uppercase rounded-30">
                //   Unavailable
                // </span>
                <Badge text="Unavailable" color="red" className="absolute bottom-2.5 right-2.5" size="sm" />
              )}

              {option.available_to_purchase && option.price > 0 && (
                <span
                  className={classNames(
                    "absolute bottom-1.5 right-1.5 bg-white text-black-secondary font-medium px-1.5 py-1 rounded-5 text-xxs ",
                    {
                      "opacity-0 pointer-events-none": showThisDropUp(option.id),
                      "opacity-100": !showThisDropUp(option.id),
                    }
                  )}
                >
                  {formatAsCurrency(getActualPrice(option))}
                </span>
              )}
            </div>
          </figure>
        ))}
      </div>
    </div>
  );
};

interface OverlaySelectorProps {
  option: VariantItem;
  index: number;
  internalSelected: VariantsInternalStateItem[];
  formatAsCurrency: FormatCurrencyFun;
  toggleDropUp: (id: string, state?: boolean) => void;
  showThisDropUp: (id: string) => boolean;
  variantIsSelected: (id: string) => boolean;
  deleteOption: (index: number) => void;
  updateOptionQuantity: (index: number, quantity: number) => void;
}

const OverlaySelector = ({
  option,
  index,
  internalSelected,
  formatAsCurrency,
  toggleDropUp,
  showThisDropUp,
  variantIsSelected,
  deleteOption,
  updateOptionQuantity,
}: OverlaySelectorProps) => {
  const ref = useRef<HTMLDivElement>(null);

  useClickOutside(ref, () => toggleDropUp(option.id, false));

  return (
    <div
      className={classNames(`bg-white px-2 flex-col`, "transition-all ease-out duration-300", {
        "rounded-5 inline-flex w-fit": !showThisDropUp(option.id),
        "top-0.5 left-0.5 right-0.5 absolute z-[999] rounded-10 flex": showThisDropUp(option.id),
      })}
      onClick={(e) => e.stopPropagation()}
      ref={ref}
    >
      <div
        className={classNames("flex-col", {
          "inline-flex": !showThisDropUp(option.id),
          flex: showThisDropUp(option.id),
        })}
      >
        <div className="inline-flex items-center gap-1 cursor-pointer py-0.5" onClick={(e) => toggleDropUp(option.id)}>
          {variantIsSelected(option.id) && !showThisDropUp(option.id) && (
            <span className="text-black font-semibold text-xs">+{getQuantity(index, internalSelected)}</span>
          )}
          <button
            className={`transition-all duration-100 ease-out transform ${
              showThisDropUp(option.id) ? "" : "rotate-180"
            }`}
          >
            {/* prettier-ignore */}
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="text-dark">
            <path
              d="M5 7.5L10 12.5L15 7.5"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          </button>
        </div>

        <div
          className={classNames("flex flex-col gap-1 transition-all duration-300 ease-out", {
            "opacity-0 h-0 w-0 pointer-events-none": !showThisDropUp(option.id),
            "opacity-100 w-auto h-auto": showThisDropUp(option.id),
          })}
        >
          <span className="text-black-muted text-xxs md:text-xs">Select Quantity</span>
          <span className="text-black font-bold text-xs md:text-sm">
            {formatAsCurrency(getActualPrice(option) * (getQuantity(index, internalSelected) || 1))}
          </span>

          <div className="flex items-center -mx-2  justify-between pt-1.5" onClick={(e) => e.stopPropagation()}>
            {getQuantity(index, internalSelected) < 2 ? (
              <button
                className="flex-shrink-0 h-8 w-9 sm:h-10 sm:w-11.5 rounded-bl-10 text-accent-red-500 bg-grey-fields-100 flex items-center justify-center p-1"
                onClick={() => deleteOption(index)}
              >
                {/* prettier-ignore */}
                <svg className="w-[50%]" viewBox="0 0 18 18" fill="none">
                  <path d="M15.75 4.48499C13.2525 4.23749 10.74 4.10999 8.235 4.10999C6.75 4.10999 5.265 4.18499 3.78 4.33499L2.25 4.48499" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" strokeWidth="1.5"/>
                  <path d="M6.375 3.7275L6.54 2.745C6.66 2.0325 6.75 1.5 8.0175 1.5H9.9825C11.25 1.5 11.3475 2.0625 11.46 2.7525L11.625 3.7275" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" strokeWidth="1.5"/>
                  <path d="M14.1373 6.85498L13.6498 14.4075C13.5673 15.585 13.4998 16.5 11.4073 16.5H6.5923C4.4998 16.5 4.4323 15.585 4.3498 14.4075L3.8623 6.85498" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" strokeWidth="1.5"/>
                  <path d="M7.74707 12.375H10.2446" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" strokeWidth="1.5"/>
                  <path d="M7.125 9.375H10.875" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" strokeWidth="1.5"/>
                </svg>
              </button>
            ) : (
              <button
                className="flex-shrink-0 h-8 w-9 sm:h-10 rounded-bl-10 sm:w-11.5 bg-grey-fields-100 inline-flex items-center justify-center text-placeholder disabled:cursor-not-allowed"
                disabled={getQuantity(index, internalSelected) < 2}
                onClick={() => updateOptionQuantity(index, getQuantity(index, internalSelected) - 1)}
              >
                {/* prettier-ignore */}
                <svg className="w-[60%]" viewBox="0 0 18 18" fill="none">
                  <path d="M4.5 9H13.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            )}

            <div className=" w-10 h-8 sm:w-11.5 sm:h-10 flex items-center justify-center bg-grey-fields-100">
              <span className="text-black font-bold text-xs md:text-sm">{getQuantity(index, internalSelected)}</span>
            </div>
            <button
              onClick={() => updateOptionQuantity(index, getQuantity(index, internalSelected) + 1)}
              className="flex-shrink-0 h-8 w-9 bg-grey-fields-100 sm:w-11.5 sm:h-10 rounded-br-10 inline-flex items-center justify-center text-placeholder"
            >
              {/* prettier-ignore */}
              <svg className="w-[60%]" viewBox="0 0 18 18" fill="none">
                <path d="M4.5 9H13.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M9 13.5V4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const getQuantity = (index: number, internalSelected: VariantsInternalStateItem[]) => {
  return internalSelected[index]?.quantity ?? 0;
};

export default SelectImageVariant;
